import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import { SuggestionProps } from '@tiptap/suggestion';
import { AnimatePresence, motion } from 'framer-motion';
import {
  memo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { Box, InputGroup, InputRightElement } from '@vimeo/bokeh';
import { Send } from '@vimeo/bokeh-icons';

import { RichTextAreaPropsType } from '../../types';
import { findSuggestionMatch } from '../Mention/findSuggestionMatch';
import MentionExtension from '../Mention/MentionExtension';

export const RichTextArea = memo(
  forwardRef(
    (
      {
        autoFocus = false,
        richtext = '',
        isExpanded = false,
        placeholder,
        isPublic,
        onFocus,
        setCommentText,
        mentionExtensionItemCallback,
        onMentionStart,
        onMentionUpdate,
        onMentionsExit,
        onKeyUpHandler,
        onKeyDownHandler,
        onMentioneeAdd: onMentionAdd,
        onMentioneeRemove: onMentionRemove,
        onSubmit: onSubmitCallbacks,
        onSubmitComment,
        customRichTextAreaStyles,
        teamOwnerId,
        commentContextFields,
        isDisabled = false,
        annotationTimeCode,
      }: RichTextAreaPropsType,
      ref,
    ) => {
      const mentionPropsRef = useRef<Partial<SuggestionProps>>();
      const mentionQueryRef = useRef('');
      const stopFocusRef = useRef(false);
      const sourceFilter = isPublic ? 'viewer' : 'collaborator';
      const [textIndent, setTextIndent] = useState<number>(0);

      const timeCodeRef = useCallback((node: HTMLSpanElement | null) => {
        if (node !== null) {
          setTextIndent(node.offsetWidth);
        }
      }, []);

      const richContent = useMemo(() => {
        try {
          return JSON.parse(richtext);
        } catch (_e) {
          return;
        }
      }, [richtext]);

      const editor = useEditor({
        editorProps: {
          handleDOMEvents: {
            keyup: (view, event) => onKeyUpHandler?.(mentionQueryRef.current, view, event),
            keydown: (view, event) =>
              onKeyDownHandler?.(mentionQueryRef.current, view, event, onSubmitComment),
          },
          handleClick: (editorView) => {
            if (!editorView.hasFocus() && !isDisabled) onFocus();
          },
        },
        editable: !isDisabled,
        extensions: [
          Document,
          Bold,
          Text,
          Paragraph,
          HardBreak,
          MentionExtension.configure({
            onMentionAdd,
            onMentionRemove,
            suggestion: {
              allowedPrefixes: null,
              allowSpaces: false,
              findSuggestionMatch,
              items: ({ query }) => {
                if (query.length) {
                  setTimeout(() =>
                    mentionExtensionItemCallback?.(query, sourceFilter, teamOwnerId),
                  );
                }
                mentionQueryRef.current = query;

                return [];
              },
              render: () => ({
                onStart: (suggestionProps) => {
                  mentionPropsRef.current = suggestionProps;
                  stopFocusRef.current = true;
                  onMentionStart?.();
                },
                onUpdate: (suggestionProps) => {
                  mentionPropsRef.current = suggestionProps;
                  onMentionUpdate?.();
                },
                onExit: (suggestionProps) => {
                  mentionPropsRef.current = suggestionProps;
                  stopFocusRef.current = false;
                  onMentionsExit?.();
                },
              }),
            },
          }),
          Placeholder.configure({
            placeholder,
            showOnlyWhenEditable: false,
          }),
        ],
        onFocus: () => {
          onFocus();
        },
        onUpdate: ({ editor }) => {
          const richText = editor.getJSON();
          setCommentText(JSON.stringify(richText || ''));
        },
        content: richContent,
      });

      useEffect(() => {
        if (autoFocus) editor?.commands?.focus?.('end');
      }, [editor, autoFocus]);

      useImperativeHandle(
        ref,
        () => {
          return {
            clearContent: () => {
              setTimeout(() => {
                editor?.commands?.clearContent();
              }, 0);
            },
            addMentionNode: (props: {
              email?: string;
              name?: string;
              userId?: number;
              teamUserId?: number;
              guestUserId?: string;
            }) => {
              return mentionPropsRef.current?.command?.(props);
            },
            selectRange: () => {
              return editor?.commands?.selectTextblockStart();
            },
            getMentionQuery: () => {
              return mentionQueryRef.current;
            },
            toggleParagraph: () => {
              return editor?.commands?.setParagraph();
            },
            toggleBold: () => {
              return editor?.commands?.toggleBold?.();
            },
            onSubmit: async () => {
              return await onSubmitCallbacks?.();
            },
            isUserMentioned: () => {
              let value = false;
              editor?.state.doc.descendants((node) => {
                if (node.type.name == 'mention') {
                  value = true;
                }
              });
              return value;
            },
            commentLength: () => {
              return editor?.getText().trim().length;
            },
            focusEnd: () => {
              if (!stopFocusRef?.current) return editor?.commands?.focus?.('end');
              return false;
            },
            removeFocus: () => {
              if (!stopFocusRef?.current) return editor?.commands?.blur();
            },
            getCommentContent: () => {
              const currentText = editor?.getJSON();
              return JSON.stringify(currentText || '');
            },
            setContent: (content: string) => {
              return editor?.commands?.setContent(content);
            },
          };
        },
        [editor, onSubmitCallbacks],
      );

      const [localOverflowY, setLocalOverflowY] = useState<'auto' | 'hidden'>('hidden');

      useEffect(() => {
        if (isExpanded) {
          // setting overflowY to auto after a delay to avoid vertical scroll during transition
          setTimeout(() => {
            setLocalOverflowY('auto');
          }, 300);
        } else {
          setLocalOverflowY('hidden');
        }
      }, [isExpanded]);

      const isReplyOrEdit = commentContextFields?.isReply || commentContextFields?.isEdit;

      const defaultRichTextAreaStyles = useMemo(
        () => ({
          '.richtextCommentBox': {
            border: isReplyOrEdit && !isExpanded ? 'none' : '1px solid',
            borderRadius: 'input-md',
            borderColor: isDisabled
              ? 'input-stroke'
              : isExpanded
              ? 'text-primary'
              : 'input-stroke-hover',
            overflowY: 'hidden',
          },
          '.richtextCommentBox:focus-within': {
            borderWidth: isExpanded ? '2px' : '1px',
            borderColor: isExpanded ? 'text-primary' : 'input-stroke-hover',
          },
          '.ProseMirror:focus-visible': {
            outline: 'none', // default its blue when none is not specified
          },
          '.ProseMirror': {
            color: 'text-primary',
            height: isExpanded ? `78px` : `38px`,
            padding: isExpanded ? '10px 12px' : '5px 12px',
            fontWeight: 'regular',
            resize: 'none',
            backgroundColor: isExpanded ? 'surface' : 'default',
            transition: 'height 120ms ease-in-out 0s',
            overflowY: localOverflowY,
            'p:first-of-type': {
              // this is to allow space for Send icon when not expanded
              maxWidth: isExpanded ? '100%' : 'calc(100% - 20px)',
            },
            'p.is-editor-empty:first-of-type::before': {
              color: isDisabled ? 'input-stroke-hover' : 'text-secondary',
              fontSize: 'text-sm',
              content: 'attr(data-placeholder)',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
              paddingLeft: isExpanded ? '0px' : '4px',
              paddingTop: isExpanded ? '0px' : '3px',
            },
          },
        }),
        [isExpanded, isDisabled, localOverflowY, isReplyOrEdit],
      );

      const annotationIndentStyles = {
        textIndent: `${textIndent}px`,
        lineHeight: '1.6',
      };

      const richTextEditorStyles = annotationTimeCode ? annotationIndentStyles : {};

      return (
        <AnimatePresence>
          <InputGroup
            display="inline-block"
            mb="2"
            sx={customRichTextAreaStyles || defaultRichTextAreaStyles}
            as={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto', transition: { type: 'tween' } }}
            exit={{ opacity: 0, height: 0 }}
          >
            {annotationTimeCode ? (
              <FormInputTimecode ref={timeCodeRef}>{annotationTimeCode}</FormInputTimecode>
            ) : null}
            <EditorContent
              className="richtextCommentBox"
              style={richTextEditorStyles}
              content={richtext}
              editor={editor}
            />
            {!isExpanded ? (
              <InputRightElement opacity="0.6" color="text-primary">
                <Send
                  color={isDisabled ? 'input-stroke-hover' : 'text-primary'}
                  height="20"
                  width="20"
                />
              </InputRightElement>
            ) : null}
          </InputGroup>
        </AnimatePresence>
      );
    },
  ),
);

const FormInputTimecode = forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => {
    return (
      <Box
        as="span"
        ref={ref}
        color={'text-primary'}
        fontWeight="bold"
        left="0"
        lineHeight="1.6"
        position="absolute"
        top="0"
        _after={{
          content: "'-'",
          display: 'inline-block',
          fontWeight: 'normal',
          padding: `0 2px`,
        }}
      >
        {children}
      </Box>
    );
  },
);
